import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Checkbox } from "@/components/ui/checkbox";
import { ReviewQueue } from "@/components/review/ReviewQueue";
import { ReviewPanel } from "@/components/review/ReviewPanel";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function ReviewPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showRejected, setShowRejected] = useState(false);
  const [collectionId, setCollectionId] = useState<string | undefined>(undefined);

  const utils = trpc.useUtils();

  const { data: collections } = trpc.collections.list.useQuery({});

  // Fetch review queue — pass collectionId to let the server filter
  const { data: locations, isLoading } = trpc.locations.reviewQueue.useQuery(
    { showRejected, collectionId },
  );

  // Use the server-side count for accurate "remaining" display
  const { data: countData } = trpc.locations.reviewCount.useQuery({ collectionId });

  // Auto-select first location when queue loads or collection changes
  useEffect(() => {
    if (locations && locations.length > 0 && !selectedId) {
      setSelectedId(locations[0]!.id);
    }
  }, [locations, selectedId]);

  const handleCollectionChange = (value: string) => {
    setCollectionId(value === "all" ? undefined : value);
    setSelectedId(null);
  };

  const advanceToNext = () => {
    if (!locations || locations.length === 0) {
      setSelectedId(null);
      return;
    }

    const currentIndex = locations.findIndex((l) => l.id === selectedId);
    const nextIndex = currentIndex >= 0 && currentIndex < locations.length - 1
      ? currentIndex + 1
      : 0;
    const next = locations[nextIndex];
    if (next && next.id !== selectedId) {
      setSelectedId(next.id);
    } else {
      setSelectedId(null);
    }
  };

  const handleSaved = () => {
    advanceToNext();
    utils.locations.reviewQueue.invalidate();
    utils.locations.reviewCount.invalidate();
    utils.locations.getById.invalidate();
  };

  const handleRejected = () => {
    advanceToNext();
    utils.locations.reviewQueue.invalidate();
    utils.locations.reviewCount.invalidate();
  };

  const handleSkipped = () => {
    advanceToNext();
  };

  const reviewCount = countData?.count ?? locations?.length ?? 0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Review</h1>
          {collections && collections.length > 0 && (
            <Select value={collectionId ?? "all"} onValueChange={handleCollectionChange}>
              <SelectTrigger className="w-[200px] h-8 text-sm">
                <SelectValue placeholder="All Collections" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Collections</SelectItem>
                {collections.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: c.color ?? "#6b7280" }}
                      />
                      {c.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!isLoading && (
            <span className="text-sm text-muted-foreground">
              ({reviewCount} remaining)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-rejected"
            checked={showRejected}
            onCheckedChange={(checked) => setShowRejected(checked === true)}
          />
          <label htmlFor="show-rejected" className="text-sm text-muted-foreground cursor-pointer">
            Show rejected
          </label>
        </div>
      </div>

      {/* Split panel */}
      {isLoading ? (
        <div className="flex-1 flex gap-4 p-4 min-h-0">
          <div className="w-80 space-y-2">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
          <div className="flex-1 space-y-4">
            <Skeleton className="aspect-video w-full rounded-lg" />
            <Skeleton className="h-40 w-full rounded-lg" />
          </div>
        </div>
      ) : reviewCount === 0 ? (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <p className="text-lg font-medium">All caught up!</p>
            <p className="text-sm mt-1">No locations need review right now.</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 min-h-0">
          {/* Left panel — queue */}
          <div className="w-80 shrink-0 border-r overflow-hidden box-border p-0">
            <ReviewQueue
              locations={locations ?? []}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </div>

          {/* Right panel — detail + form */}
          <div className="flex-1 min-w-0 overflow-hidden">
            {selectedId ? (
              <ReviewPanel
                locationId={selectedId}
                collectionId={collectionId}
                onSaved={handleSaved}
                onRejected={handleRejected}
                onSkipped={handleSkipped}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                Select a location from the queue to review.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
